import React, { useState, useRef, useEffect, Ref } from 'react';
import { useReactToPrint } from 'react-to-print';
import { httpGet, httpPost } from '../../../modules/Backend';
import { getCurrentFormattedDate, displayDateTime } from '../../../helpers/DateHelper';
import LoadingButton from '../LoadingButton';
import { joinAddressString } from "../../../helpers/StringHelper";
import { Intent } from '../recallWizard/recallData';
import { EntityNames } from '../../../enums/EntityNames';

/** Within one inventory group: part/whole cases by year then parseInt case ref when both parse; whole-box rows are not ordered by year/ref (only listed after cases, relative order unchanged). */
function comparePickListInventoryItems(a: any, b: any): number {
  const aIsCase = a.intent === Intent.PartCase || a.intent === Intent.WholeCase;
  const bIsCase = b.intent === Intent.PartCase || b.intent === Intent.WholeCase;
  if (aIsCase !== bIsCase) {
    return aIsCase ? -1 : 1;
  }
  if (!aIsCase) {
    return 0;
  }
  const yearA = Number(a.year);
  const yearB = Number(b.year);
  const yA = Number.isFinite(yearA) ? yearA : 0;
  const yB = Number.isFinite(yearB) ? yearB : 0;
  if (yA !== yB) {
    return yA - yB;
  }
  const refA = a.caseReference != null ? String(a.caseReference).trim() : '';
  const refB = b.caseReference != null ? String(b.caseReference).trim() : '';
  const numA = Number.parseInt(refA, 10);
  const numB = Number.parseInt(refB, 10);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return numA - numB;
  }
  return 0;
}

interface PrintableComponentProps {
  data: any;
  locationSummary: any;
  ref: React.RefObject<HTMLDivElement>
}

// Printable component with a ref to allow printing
const PrintableComponent = React.forwardRef<HTMLDivElement, PrintableComponentProps>(({ data, locationSummary }, ref) => (

  <div ref={ref}>
    {data && <div className='print-pick-list__wrapper'>
      <div className='print-pick-list__header'>
        <p>{getCurrentFormattedDate()}</p>
      </div>
      <div className='print-pick-list__information'>
        <div className='print-pick-list__information-row'>
          <h1 className='print-pick-list__information-title'>Recall</h1>
          <h1 className='print-pick-list__information-title'>{data.accountName}</h1>
          <h1 className='print-pick-list__information-title'>{data.accountReference}</h1>
        </div>
        <div className='print-pick-list__information-row'>
          <h2 className='print-pick-list__information-item'>Our Ref: {data.reference}</h2>
          <h2 className='print-pick-list__information-item'>Client Ref: {data.orderNumber}</h2>
        </div>
        <div className='print-pick-list__information-row'>
          <h2 className='print-pick-list__information-item'>Added On: {data.addedOn ? displayDateTime(data.addedOn) : '--'}</h2>
          <h2 className='print-pick-list__information-item'>
            Requested By: {data.requestedBy}
            {data.onBehalfOf && data.onBehalfOf !== data.requestedBy && ` | On Behalf Of: ${data.onBehalfOf}`}
          </h2>
        </div>
        <div className='print-pick-list__information-row'>
          <h2 className='print-pick-list__information-item'>Contact Number: {data.contactNumber ?? data.ContactNumber ?? '--'}</h2>
        </div>
        {data.invoiceAddress && (
          <div className='print-pick-list__information-row'>
            <h2 className='print-pick-list__information-item'>Invoice Address: {joinAddressString(data?.invoiceAddress)}</h2>
          </div>
        )}
        {data.deliveryAddress && (
          <div className='print-pick-list__information-row'>
            <h2 className='print-pick-list__information-item'>Deliver To: {joinAddressString(data?.deliveryAddress)}</h2>
          </div>
        )}
        {(data.deliveryMethod || data.DeliveryMethod) && (
          <div className='print-pick-list__information-row'>
            <h2 className='print-pick-list__information-item'>Delivery Method: {data.deliveryMethod || data.DeliveryMethod}</h2>
          </div>
        )}
      </div>
      {locationSummary && locationSummary.locations && (
        <div className="print-pick-list__location-summary">
          <div className="print-pick-list__location-summary-columns">
            {locationSummary.locations.map((rack: any) => (
            <div key={rack.breadcrumb} className="print-pick-list__location-summary-item">
              <div className="pick-list-summary-print__rack">
                <div className="pick-list-summary-print__dot pick-list-summary-print__dot"></div>
                <p className="small">{rack.breadcrumb}</p>
              </div>
              {rack.outerCubes.map((oc: any) => {
                const sortedPackingBoxes = [...(oc.packingBoxes || [])].sort((a: any, b: any) => 
                  (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
                );
                return (
                  <div key={oc.name}>
                    <div className="pick-list-summary-print__outer-cube">
                      <div className="pick-list-summary-print__dot pick-list-summary-print__dot"></div>
                      <p className="small">{oc.name}</p>
                    </div>
                    {sortedPackingBoxes.map((pb: any) => (
                      <div key={pb.name} className="pick-list-summary__packing-box">
                        <div className="pick-list-summary-print__dot pick-list-summary-print__dot"></div>
                        <p className="small">{pb.name}</p>
                        {pb.sequenceNumber !== undefined && (
                          <p className="small small--2 margin__left--s">Seq: {pb.sequenceNumber}</p>
                        )}
                        {pb.volumeVNumber && (
                          <p className="small small--2 margin__left--s">Vol: {pb.volumeVNumber}</p>
                        )}
                        <p className="small small--2 margin__left--s">{pb.itemQuantity} {pb.itemQuantity === 1 ? "item" : "items"}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>))}
          </div>
        </div>
      )}
      {data && data.locations.map((location: any, i: number) => {
        // Group items by their breadcrumb (inventory groups)
        const inventoryGroups = location.items.reduce((groups: any, item: any) => {
          const breadcrumb = item.breadcrumb || location.breadcrumb;
          if (!groups[breadcrumb]) {
            groups[breadcrumb] = [];
          }
          groups[breadcrumb].push(item);
          return groups;
        }, {});

        // Get box information from first item in location
        const firstItem = location.items[0];
        const boxSequence = firstItem?.boxSequence;
        const boxVolume = firstItem?.boxVolume;
        
        const boxInfoParts = [];
        if (boxSequence !== undefined && boxSequence !== null) {
          boxInfoParts.push(`Seq: ${boxSequence}`);
        }
        if (boxVolume) {
          boxInfoParts.push(boxVolume);
        }
        const boxInfoText = boxInfoParts.length > 0 ? ` - ${boxInfoParts.join(', ')}` : '';

        return (
          <div key={i} className='print-pick-list__location-group'>
            <div className='print-pick-list__location'>
              <p>{location.breadcrumb}{boxInfoText}</p>
            </div>
            {Object.entries(inventoryGroups).map(([inventoryBreadcrumb, items]: [string, any]) => {
              const sortedItems = [...items].sort(comparePickListInventoryItems);
              return (
              <div key={inventoryBreadcrumb} className='print-pick-list__inventory-group'>
                {sortedItems.map((item: any, j: number) => (
                  <div key={j} className='print-pick-list__item-wrapper'>
                    <div className='print-pick-list__item-information-wrapper'>
                      {(item.intent === Intent.PartCase || item.intent === Intent.WholeCase) &&
                        <div className='print-pick-list__pairs-container'>
                          <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                            <p className='small-2'>Case</p>
                            <p className='small-10'>{item.caseReference}</p>
                          </div>
                          <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                            <p className='small-2'>Specimen</p>
                            <p className='small-10'>{item.specimenReference}</p>
                          </div>
                          <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                            <p className='small-2'>Year</p>
                            <p className='small-10'>{item.year}</p>
                          </div>
                          {item.rangeNotes && (
                            <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                              <p className='small-2'>Range Notes</p>
                              <p className='small-10'>{item.rangeNotes}</p>
                            </div>
                          )}
                          <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                            <p className='small-2'>Range</p>
                            <p className='small-10'>{item.range}</p>
                          </div>
                          {item.requestorComments && (
                            <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                              <p className='small-2'>Customer Comments</p>
                              <p className='small-10'>{item.requestorComments}</p>
                            </div>
                          )}
                          {item.itemNotes && (
                            <div className='grid-x print-pick-list__compact-row print-pick-list__right-column'>
                              <p className='small-2'>CellNass Notes</p>
                              <p className='small-10'>{item.itemNotes}</p>
                            </div>
                          )}
                          <div className='grid-x print-pick-list__compact-row print-pick-list__left-column'>
                            <p className='small-2'>Type</p>
                            <p className='small-10'>{item.type}</p>
                          </div>
                        </div>
                      }
                      {item.intent === Intent.WholeBox &&
                        <div className='print-pick-list__pairs-container'>
                          <div className='grid-x print-pick-list__compact-row'>
                            <p className='small-2'>Whole Box</p>
                            <p className='small-10'>{item.boxReference}</p>
                          </div>
                          <div className='grid-x print-pick-list__compact-row'>
                            <p className='small-2'>Sequence number</p>
                            <p className='small-10'>{item.boxSequence}</p>
                          </div>
                          <div className='grid-x print-pick-list__compact-row'>
                            <p className='small-2'>Volume</p>
                            <p className='small-10'>{item.boxVolume}</p>
                          </div>
                        </div>
                      }
                    </div>
                    <div className='print-pick-list__barcode-wrapper'>
                      <div className='print-pick-list__barcode-information-wrapper'>
                        {(item.intent === Intent.PartCase || item.intent === Intent.WholeCase) &&
                          <>
                            <div className='grid-x'>
                              <p className='xsmall small-6'>{location.name}</p>
                              <p className='xsmall small-3'>Range</p>
                              <p className='xsmall small-3'>{item.range}</p>
                            </div>
                            <div className='grid-x'>
                              <p className='xsmall small-3'>Case</p>
                              <p className='xsmall small-3'>{item.caseReference}</p>
                              <p className='xsmall small-3'>Specimen</p>
                              <p className='xsmall small-3'>{item.specimenReference}</p>
                            </div>
                            <div className='grid-x'>
                              <p className='xsmall small-3'>Year</p>
                              <p className='xsmall small-3'>{item.year}</p>
                            </div>
                            <div className='grid-x'>
                              <p className='xsmall small-3'>Type</p>
                              <p className='xsmall small-3'>{item.type}</p>
                            </div>
                            <div className='grid-x'>
                              <p className='xsmall small-12'>Requested By {data.requestedBy}</p>
                              <p className='xsmall small-12'>On {data.addedOn ? displayDateTime(data.addedOn) : '--'}</p>
                            </div>
                          </>
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
            })}
          </div>
        );
      })
      }
    </div>}
  </div>
)
);

interface PrinterProps {
  recallId: string;
  save: Function;
}

function PrintRecallPickList({ recallId, save }: PrinterProps) {
  const [data, setData] = useState<any[]>();
  const [locationSummary, setLocationSummary] = useState<any>({});
  const [shouldPrint, setShouldPrint] = useState(false);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    const [printDataResponse, locationSummaryResponse] = await Promise.all([
      httpGet(`api/${EntityNames.recalls}/pickPrintData?recallId=${recallId}`),
      httpGet(`api/${EntityNames.recalls}/locationSummary?recallId=${recallId}&toPickOnly=true`)
    ]);

    if (printDataResponse.data && locationSummaryResponse.data) {
      setData(printDataResponse.data);
      setLocationSummary(locationSummaryResponse.data);
      setShouldPrint(true);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforePrint: async () => {
      try {
        await httpPost(
          `api/${EntityNames.recalls}/markPickListPrinted`,
          { id: recallId },
          undefined,
          undefined,
          true // disableToast – no success/response toast when marking as printed
        );
      } catch {
        // Toast handled by httpPost on error; avoid re-throwing
      }
    },
    onAfterPrint: () => {
      setShouldPrint(false);
      setLoading(false);
    }
  });

  useEffect(() => {
    if (shouldPrint && componentRef.current) {
      handlePrint();
    }
  }, [shouldPrint]);

  const handleFetchAndPrint = async () => {
    if (loading)
      return
    setLoading(true)
    await save();
    await fetchData();
  };

  return (
    <div>
      <LoadingButton className="button button--primary button--with-icon button--small"
        onPress={handleFetchAndPrint}
        isDisabled={loading}
        isLoading={loading}
        loadingText="Preparing"
        buttonText="Print Pick List">
        <></>
      </LoadingButton>
      <div style={{ display: 'none' }}>
        <PrintableComponent ref={componentRef} data={data} locationSummary={locationSummary} />
      </div>
    </div>
  );
};

export default PrintRecallPickList;